// ds-color-ignore-file: system font stack in dev/internal file
"use client"

import { useEffect, useState } from "react"
import { useCart, dispatchCart, countChanges, type CartItem } from "./edit-cart"
import { useApplyCart } from "./use-apply-cart"

// Remember whether the change-list was expanded, so it defaults to the
// reviewer's last preference (collapsed out of the box → more room for the
// to-translate strings). (AHD-7494)
const LIST_OPEN_KEY = "ahead-cart-list-open"

/**
 * Cockpit-rail cart: lists staged edits (collapsible, each clickable to
 * re-edit or delete), a preview toggle, the accumulating-PR target note, and
 * Apply. The outer wrapper uses className="ahead-cockpit-cart" (no fixed
 * positioning) so the cockpit lays it out. Apply POSTs to /api/edit/pr +
 * /api/edit/cms with the SSO idToken in
 * a `User-Authorization` header — verified server-side by ahead-core's
 * authorizeSystemAccess.
 */
export function CockpitCart({
  identity,
  currentRef,
  env,
  preview,
  onTogglePreview,
  onEditItem,
}: {
  identity: { idToken: string }
  currentRef: string
  env: string
  preview: boolean
  onTogglePreview: (v: boolean) => void
  onEditItem?: (item: CartItem) => void
}) {
  const items = useCart()
  const { busy, result, setResult, apply } = useApplyCart({
    idToken: identity.idToken,
    currentRef,
    env,
  })
  const [listOpen, setListOpen] = useState(
    () =>
      (typeof window !== "undefined"
        ? localStorage.getItem(LIST_OPEN_KEY)
        : null) === "1"
  )
  useEffect(() => {
    localStorage.setItem(LIST_OPEN_KEY, listOpen ? "1" : "0")
  }, [listOpen])
  // ⌘/Ctrl+S applies — but only when no in-panel string editor is open (that
  // editor claims ⌘S for "add to cart"); the open-editor DOM probe disambiguates.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")))
        return
      if (document.querySelector("[data-ahead-edit-panel] textarea")) return
      e.preventDefault()
      if (!busy && items.length)
        apply(
          items.filter((i) => i.source === "intl"),
          items.filter((i) => i.source === "cms")
        )
    }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, items.length])
  // Keep the cart mounted after a successful apply so the PR link / result stays
  // visible (the cart clears on success, but the result must persist).
  if (!items.length && !result) return null
  const intlItems = items.filter((i) => i.source === "intl")
  const cmsItems = items.filter((i) => i.source === "cms")

  const applyLabel = busy
    ? "Saving…"
    : cmsItems.length && !intlItems.length
      ? `Save ${cmsItems.length} CMS draft${cmsItems.length > 1 ? "s" : ""}`
      : "Apply changes"

  return (
    <div data-ahead-editor-ui className="ahead-cockpit-cart">
      {items.length ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <strong style={{ fontSize: 13 }}>
              {/* Count changed strings (per-locale values), matching the PR's
                  file count — not cart rows, which are per-key. */}
              Ahead edit cart ({countChanges(items)})
            </strong>
            <button
              onClick={() => setListOpen((o) => !o)}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 11,
                color: "#2b6cff",
                fontWeight: 600,
              }}
            >
              {listOpen ? "Hide ▾" : `Show ${items.length} ▸`}
            </button>
          </div>

          {listOpen ? (
            <ul
              style={{
                maxHeight: 230,
                overflow: "auto",
                margin: "8px 0",
                padding: 0,
                listStyle: "none",
                fontSize: 12,
              }}
            >
              {items.map((i) => (
                <li
                  key={i.key}
                  style={{
                    marginBottom: 4,
                    padding: "5px 6px",
                    borderRadius: 7,
                    border: "1px solid #eee",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 6,
                    }}
                  >
                    <button
                      onClick={() => onEditItem?.(i)}
                      title="Edit this change"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: "left",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <code>
                        {i.source === "cms"
                          ? i.key
                          : i.key.split(".").slice(-2).join(".")}
                      </code>{" "}
                      <span style={{ color: "#aaa" }}>
                        ({Object.keys(i.values).join(",")})
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        dispatchCart({ type: "remove", key: i.key })
                      }}
                      title="Remove from cart"
                      aria-label="Remove from cart"
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "#b00",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: "0 2px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {/* Pre-apply rail: exact write target + old→new, so nothing
                  changes blind. */}
                  <div
                    style={{
                      fontSize: 10,
                      color: "#888",
                      fontFamily: "ui-monospace, monospace",
                    }}
                  >
                    {i.source === "cms"
                      ? `→ ${i.cms?.collection}/${i.cms?.id}.${i.cms?.field} (draft)`
                      : `→ ${Object.keys(i.values)
                          .map((l) => `messages/${l}.json`)
                          .join(", ")}`}
                  </div>
                  {/* A whole-field rich-text (Lexical) edit carries `fullValue`
                      (a tree); its `values` hold only an opaque placeholder, so
                      showing the placeholder old→new diff is meaningless. Show a
                      human label instead. (AHD-7515) */}
                  {i.fullValue ? (
                    <div style={{ fontSize: 10, color: "#555" }}>
                      Article body — rich text edited
                    </div>
                  ) : (
                    Object.entries(i.values).map(([l, v]) => (
                      <div key={l} style={{ fontSize: 10, color: "#555" }}>
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#b00",
                          }}
                        >
                          {v.old.slice(0, 40)}
                        </span>
                        {" → "}
                        <span style={{ color: "#070" }}>
                          {v.new.slice(0, 40)}
                        </span>
                      </div>
                    ))
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ height: 8 }} />
          )}

          <label
            style={{
              display: "flex",
              gap: 8,
              fontSize: 12,
              alignItems: "center",
            }}
          >
            <input
              type="checkbox"
              checked={preview}
              onChange={(e) => onTogglePreview(e.target.checked)}
            />
            Preview changes on page
          </label>
          {intlItems.length ? (
            <div
              style={{
                fontSize: 11,
                fontFamily: "ui-monospace, monospace",
                color: "#444",
                margin: "8px 0",
              }}
            >
              → your content PR (base preflight)
            </div>
          ) : null}
          {cmsItems.length ? (
            <div
              style={{
                fontSize: 11,
                fontFamily: "ui-monospace, monospace",
                color: "#2f6f3e",
                margin: "8px 0",
              }}
            >
              {cmsItems.length} CMS edit{cmsItems.length > 1 ? "s" : ""} →
              Payload draft (publish gate)
            </div>
          ) : null}
          <button
            onClick={() => apply(intlItems, cmsItems)}
            disabled={busy}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              border: "none",
              borderRadius: 9,
              padding: "10px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? "default" : "pointer",
              background: "#0b0b0f",
              color: "#fff",
              width: "100%",
            }}
          >
            {applyLabel}
            {busy ? null : (
              <kbd
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  opacity: 0.7,
                  border: "1px solid rgba(255,255,255,.5)",
                  borderRadius: 4,
                  padding: "0 4px",
                }}
              >
                ⌘S
              </kbd>
            )}
          </button>
        </>
      ) : null}
      {/* Post-apply result. A success only shows once the cart is empty, so a
          fresh edit replaces it instead of letting a stale "Done" linger. A
          FAILURE shows even while items remain (the cart isn't cleared on error)
          — otherwise an apply that errored looks like it silently did nothing. */}
      {result && (!result.ok || !items.length) ? (
        <div style={{ fontSize: 12, marginTop: items.length ? 8 : 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <strong
              style={{ fontSize: 12, color: result.ok ? "#070" : "#b00" }}
            >
              {result.ok ? "Done" : "Couldn’t apply"}
            </strong>
            <button
              onClick={() => setResult(null)}
              aria-label="Dismiss"
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                color: "#999",
                fontSize: 15,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          {result.prUrl ? (
            <a
              href={result.prUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                color: "#fff",
                background: "#2b6cff",
                borderRadius: 7,
                padding: "7px 12px",
                fontWeight: 700,
                textDecoration: "none",
                marginBottom: 4,
              }}
            >
              View PR →
            </a>
          ) : null}
          <div style={{ wordBreak: "break-all", color: "#444", marginTop: 4 }}>
            {result.msg}
          </div>
        </div>
      ) : null}
    </div>
  )
}
